import { LoadingButton } from '@mui/lab';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Stack,
    Tooltip,
    Typography,
} from '@mui/material';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import { HelpOutline, Lock, LockOpen, SaveAlt } from '@mui/icons-material';
import { useState } from 'react';

export const ForceOffsetHeader = ({
    productId,
    disabled,
    loading,
    unlocked,
    onSave,
    unlock,
}: {
    productId: string;
    disabled: boolean;
    loading: boolean;
    unlocked: boolean;
    onSave: () => void;
    unlock: (value: boolean) => void;
}) => {
    const [infoDialog, setInfoDialog] = useState(false);

    return (
        <Box
            display={'flex'}
            flexDirection={'row'}
            alignItems={'center'}
            justifyContent={'flex-end'}
            width={'100%'}
            mb={1}>
            <Stack direction='row' alignItems='center' spacing={1}>
                {/* Save */}
                <Tooltip title={disabled || !unlocked ? '' : 'Save override'}>
                    <LoadingButton
                        disabled={disabled || !unlocked}
                        loading={loading}
                        variant='contained'
                        color='primary'
                        onClick={onSave}
                        sx={{ minWidth: 40 }}>
                        <SaveAlt fontSize='small' />
                    </LoadingButton>
                </Tooltip>

                {/* Lock / Unlock */}
                <Tooltip title={disabled ? 'Upload product first' : unlocked ? 'Lock editing' : 'Unlock to edit'}>
                    <IconButton disabled={disabled} onClick={() => unlock(!unlocked)}>
                        {unlocked ? <LockOpen fontSize='small' /> : <Lock fontSize='small' />}
                    </IconButton>
                </Tooltip>

                {/* Info */}
                <Tooltip title='Need more info?'>
                    <IconButton color='primary' onClick={() => setInfoDialog(true)}>
                        <HelpOutline fontSize='small' />
                    </IconButton>
                </Tooltip>
            </Stack>

            <Dialog open={infoDialog} fullWidth>
                <DialogTitle>
                    <Typography variant='h4'>Force Offset Process</Typography>
                </DialogTitle>

                <DialogContent>
                    <Typography variant={'h5'} pb={'10px'} fontWeight={'bold'}>
                        Background
                    </Typography>
                    <Typography>
                        Force Offset is adjusting the force at which the cutter cuts the film, which affects the blade
                        depth. When the blade is too deep or too shallow, it causes issues with peeling. This process
                        describes how to adjust the force offset.
                    </Typography>
                    <Typography variant={'h5'} pt={'10px'} pb={'10px'} fontWeight={'bold'}>
                        What to do?
                    </Typography>
                    <Typography>The first step is to determine if the force is too deep or too shallow.</Typography>
                    <Typography>
                        <i>Shallow</i> ={'>'} adjust the force offset in the positive direction.
                    </Typography>
                    <Typography>
                        <i>Deep</i> ={'>'} adjust the force offset in the negative direction.
                    </Typography>
                    <Typography variant={'h5'} pt={'10px'} pb={'10px'} fontWeight={'bold'}>
                        How much to adjust?
                    </Typography>
                    <Typography>
                        The amount to adjust is depends on the machine, different machines have different ranges. The
                        recommended increments for the following machines are
                    </Typography>
                    <ul style={{ paddingLeft: '20px', paddingTop: '0px' }}>
                        <li>
                            <Typography>
                                <b>
                                    <i>RC2.1 (Any GraphTec)</i>
                                </b>
                                : 1-2
                            </Typography>
                        </li>
                        <li>
                            <Typography>
                                <b>
                                    <i>RC3.0 (ADSI)</i>
                                </b>
                                : 1-2
                            </Typography>
                        </li>
                        <li>
                            <Typography>
                                <b>
                                    <i>RCMini</i>
                                </b>
                                : 5-10
                            </Typography>
                        </li>
                        <li>
                            <Typography>
                                <b>
                                    <i>RC 4 (Bamboo)</i>
                                </b>
                                : 5-10
                            </Typography>
                        </li>
                    </ul>

                    <Typography variant={'h5'} pt={'10px'} pb={'10px'} fontWeight={'bold'}>
                        What Next?
                    </Typography>

                    <Typography>Keep adjusting until the machine is cutting the film properly.</Typography>
                    <Typography>
                        Make sure the customer is connected to internet, and that you wait 90 seconds after each
                        adjustment to ensure the machine has time to update the offset.
                    </Typography>
                </DialogContent>

                <DialogActions>
                    <Button variant={'contained'} onClick={() => setInfoDialog(false)}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};
